import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { nanoid as generate } from 'nanoid';
import { hyphenateText } from 'screens/Applications&WorkOrders/Components/OperationPdf/helpers/formatHelpers';
import { FlexTableCellItem } from '../../types';
import styles from '../../styles';

type FlexTableCellProps = {
  cell: FlexTableCellItem;
  rowIndex: number;
  isLast: boolean;
  cellStyles?: Record<string, any>;
  headingStyles?: Record<string, any>;
  showBorder?: boolean;
};

const FlexTableCell = ({
  cell,
  rowIndex,
  isLast,
  cellStyles = {},
  headingStyles = {},
  showBorder = false
}: FlexTableCellProps) => {
  const baseStyle = rowIndex === 1 ? styles.tableHeaderCell : styles.tableCell;

  const combinedCellStyles = {
    ...(rowIndex <= 1 ? {} : cellStyles),
    ...cell.styles
  };

  // If width is specified, remove flex: 1 from base style
  const finalBaseStyle = combinedCellStyles.width
    ? { ...baseStyle, flex: undefined }
    : baseStyle;

  // For header row (rowIndex === 1), remove minHeight
  const styleWithHeight =
    rowIndex === 1
      ? { ...finalBaseStyle, fontSize: 10, minHeight: undefined }
      : finalBaseStyle;

  const allCellStyles = StyleSheet.create({
    styles: {
      ...styleWithHeight,
      ...combinedCellStyles,
      marginRight: isLast ? 0 : 4,
      borderRight: showBorder && !isLast ? '1px solid #DFE2E7' : 'none'
    }
  });

  const textStyle =
    rowIndex === 1
      ? {
          fontSize: 10,
          fontFamily: 'Noto Sans',
          textAlign: cell.styles?.textAlign || 'left',
          ...(cell.styles?.textAlign === 'right' ? { right: 4 } : { left: 4 })
        }
      : {};

  const textBorderStyle = {
    backgroundColor: 'transparent',
    width: 1,
    height: '100%',
    marginLeft: 'auto'
  };

  const renderLabel = () => {
    if (typeof cell.label === 'string') {
      return cell.label === '' ? (
        <Text />
      ) : (
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            textAlign: cell.styles?.textAlign || 'left',
            marginLeft: cell.styles?.textAlign === 'right' ? 'auto' : 0,
            ...(rowIndex === 1
              ? {
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  ...headingStyles
                }
              : {})
          }}
        >
          <View style={{ paddingTop: 4, paddingBottom: 4 }}>
            <Text
              style={textStyle}
              hyphenationCallback={
                cell.shouldHyphenate
                  ? (word: string) => hyphenateText(word)
                  : undefined
              }
            >
              {cell.label}
            </Text>
          </View>
          <Text style={textBorderStyle} />
        </View>
      );
    }
    return cell.label;
  };

  return (
    <View style={allCellStyles.styles} key={`table-cell-${generate()}`}>
      {renderLabel()}
    </View>
  );
};

export default FlexTableCell;
